import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Shop.css';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, doc, onSnapshot } from 'firebase/firestore';
import { getOptimizedImageUrl } from '../utils/imageOptimization';
import LazyImage from '../components/LazyImage';
const DEFAULT_PRODUCT_IMAGE = 'https://via.placeholder.com/600x800?text=Abaya';

const optimizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  return getOptimizedImageUrl(url, 'card');
};

const getProductImages = (product) => {
  const images = [];
  if (Array.isArray(product.images)) {
    product.images.forEach((img) => {
      const trimmed = img ? img.trim() : '';
      if (trimmed && !images.includes(trimmed)) {
        images.push(optimizeImageUrl(trimmed));
      }
    });
  }
  if (product.image) {
    const trimmed = product.image.trim();
    if (trimmed && !images.includes(trimmed)) {
      images.push(optimizeImageUrl(trimmed));
    }
  }
  // Also check for image2, image3, image4 for backward compatibility
  [product.image2, product.image3, product.image4].forEach(img => {
    if (img) {
      const trimmed = img.trim();
      if (trimmed && !images.includes(trimmed)) {
        images.push(optimizeImageUrl(trimmed));
      }
    }
  });
  return images;
};

// Product Card Component with Image Hover Effect
function ProductCard({ product, images, defaultImage, onClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const cardRef = useRef(null);
  const touchStartX = useRef(null);
  const hasMoved = useRef(false);
  const availabilityRaw = product.availability || 'In Stock';
  const availabilityKey = availabilityRaw.toLowerCase();
  const availabilityMarkedLow = availabilityKey.includes('few') || availabilityKey.includes('low') || availabilityKey.includes('left');
  const trackQuantity = product.trackQuantity !== false;
  const lowStockThreshold = Number(product.lowStockThreshold ?? 10);
  const stockValueRaw = product.stock ?? product.quantity;
  const stockValue = Number.isFinite(Number(stockValueRaw)) ? Number(stockValueRaw) : null;
  const isOut = availabilityKey.includes('out') || availabilityKey.includes('sold') || (trackQuantity && stockValue !== null && stockValue <= 0);
  const isLowStock = !isOut && (availabilityMarkedLow || (trackQuantity && stockValue !== null && stockValue > 0 && stockValue <= lowStockThreshold));
  const availabilityLabel = isOut ? 'Sold Out' : 'Few Pieces Left';
  const availabilityClass = isOut ? 'is-out' : 'is-low';

  const handleMouseMove = (e) => {
    if (images.length <= 1) return;
    
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const cardWidth = rect.width;
    const relativeX = e.clientX - rect.left;
    const percentage = relativeX / cardWidth;
    
    // Calculate which image to show based on mouse position
    const newIndex = Math.min(
      Math.floor(percentage * images.length),
      images.length - 1
    );
    
    if (newIndex !== currentImageIndex) {
      setCurrentImageIndex(newIndex);
    }
  };

  const handleMouseLeave = () => {
    setCurrentImageIndex(0);
  };

  const handleTouchStart = (e) => {
    if (images.length <= 1) return;
    touchStartX.current = e.touches[0].clientX;
    hasMoved.current = false;
  };

  const handleTouchMove = (e) => {
    if (images.length <= 1 || !touchStartX.current) return;
    
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const currentX = e.touches[0].clientX;
    const moveDistance = Math.abs(currentX - touchStartX.current);
    
    // If moved more than 5px, consider it a swipe
    if (moveDistance > 5) {
      hasMoved.current = true;
    }
    
    const cardWidth = rect.width;
    const relativeX = currentX - rect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / cardWidth));
    
    // Calculate which image to show based on touch position
    const newIndex = Math.min(
      Math.floor(percentage * images.length),
      images.length - 1
    );
    
    if (newIndex !== currentImageIndex) {
      setCurrentImageIndex(newIndex);
    }
  };

  const handleTouchEnd = (e) => {
    // If user swiped, prevent click navigation
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
    }
    touchStartX.current = null;
    hasMoved.current = false;
  };

  const handleClick = (e) => {
    // Prevent navigation if it was a swipe
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick();
  };

  const currentImage = images[currentImageIndex] || defaultImage;

  return (
    <div 
      ref={cardRef}
      className="product-card"
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="product-card__image-wrapper">
        <LazyImage
          src={currentImage}
          className="product-card__image"
          context="card"
        />
        {(isOut || isLowStock) && (
          <span className={`product-card__availability ${availabilityClass}`}>
            {availabilityLabel}
          </span>
        )}
        {images.length > 1 && (
          <div className="product-card__image-indicator">
            {images.map((_, index) => (
              <span
                key={index}
                className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="product-card__info">
        <span className="product-card__code">{product.name}</span>
        <span className="product-card__price">{product.price.toFixed(2)} ر.ق</span>
      </div>
    </div>
  );
}

function Shop() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [displayCount, setDisplayCount] = useState(8);
  const [sortBy, setSortBy] = useState('name-asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(true);
  const [shopHeroImage, setShopHeroImage] = useState('');
  const [isLoadingHero, setIsLoadingHero] = useState(true);

  // Fetch categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        const categoriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoriesData);
        const defaultCategory = categoriesData.find(category => {
          const label = category.label ? category.label.toLowerCase() : '';
          const id = category.id ? category.id.toLowerCase() : '';
          return label === 'abaya' || id === 'abaya';
        });
        if (defaultCategory) {
          setActiveCategory(prev => (prev ?? defaultCategory.id));
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch subcategories from Firestore
  useEffect(() => {
    const fetchSubcategories = async () => {
      setIsLoadingSubcategories(true);
      try {
        const subcategoriesRef = collection(db, 'subcategories');
        const q = query(subcategoriesRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        const subcategoriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubcategories(subcategoriesData);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setSubcategories([]);
      } finally {
        setIsLoadingSubcategories(false);
      }
    };

    fetchSubcategories();
  }, []);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    if (categoryParam) {
      setActiveCategory(categoryParam);
    } else if (!subcategoryParam) {
      setActiveCategory(null);
    }
    if (subcategoryParam) {
      setActiveSubcategory(subcategoryParam);
    } else {
      setActiveSubcategory(null);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!activeCategory && activeSubcategory) {
      const matchedSubcategory = subcategories.find(subcategory => subcategory.id === activeSubcategory);
      if (matchedSubcategory?.categoryId) {
        setActiveCategory(matchedSubcategory.categoryId);
      }
    }
  }, [activeCategory, activeSubcategory, subcategories]);

  useEffect(() => {
    if (!activeCategory) {
      if (activeSubcategory) {
        setActiveSubcategory(null);
      }
      return;
    }

    const validSubcategoryIds = new Set(
      subcategories
        .filter(subcategory => subcategory.categoryId === activeCategory)
        .map(subcategory => subcategory.id)
    );

    if (activeSubcategory && !validSubcategoryIds.has(activeSubcategory)) {
      setActiveSubcategory(null);
    }
  }, [activeCategory, activeSubcategory, subcategories]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const productsRef = collection(db, 'products');
        let snapshot;
        
        try {
          // Try with orderBy
          const q = query(productsRef, orderBy('createdAt', 'desc'));
          snapshot = await getDocs(q);
        } catch (orderError) {
          // If orderBy fails, fetch without ordering
          console.warn('Fetching without orderBy:', orderError);
          snapshot = await getDocs(productsRef);
        }
        
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort in memory if needed
        products.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.seconds - a.createdAt.seconds;
          }
          return 0;
        });
        
        setAllProducts(products);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    setIsLoadingHero(true);
    const homepageRef = doc(db, 'homepage', 'content');
    const unsubscribe = onSnapshot(
      homepageRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setShopHeroImage(data.shopHeroImage || '');
        setIsLoadingHero(false);
      },
      (error) => {
        console.error('Error fetching shop hero image:', error);
        setIsLoadingHero(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const sortOptions = [
    { id: 'name-asc', label: 'Name: A-Z' },
    { id: 'price-low', label: 'Price: Low to High' },
    { id: 'price-high', label: 'Price: High to Low' },
    { id: 'newest', label: 'Newest First' },
  ];

  const filteredProducts = useMemo(() => {
    let filtered = allProducts;

    if (activeCategory) {
      filtered = filtered.filter(product => product.category === activeCategory);
    }

    if (activeSubcategory) {
      filtered = filtered.filter(product => product.subcategory === activeSubcategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allProducts, activeCategory, activeSubcategory, searchQuery]);

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc': {
          const nameA = (a.name || '').trim();
          const nameB = (b.name || '').trim();
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        }
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'newest':
          if (a.createdAt && b.createdAt) {
            return b.createdAt.seconds - a.createdAt.seconds;
          }
          return 0;
        default:
          return 0;
      }
    });
  }, [filteredProducts, sortBy]);

  const displayedProducts = useMemo(
    () => sortedProducts.slice(0, displayCount),
    [sortedProducts, displayCount]
  );
  const hasMore = sortedProducts.length > displayCount;
  const visibleSubcategories = useMemo(() => {
    if (!activeCategory) return [];
    return subcategories.filter(subcategory => subcategory.categoryId === activeCategory);
  }, [activeCategory, subcategories]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 8);
  };

  const updateShopParams = (categoryId, subcategoryId) => {
    const params = {};
    if (categoryId) {
      params.category = categoryId;
    }
    if (subcategoryId) {
      params.subcategory = subcategoryId;
    }
    setSearchParams(params);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  return (
    <div className="shop-page">
      <section
        className={`shop-hero ${isLoadingHero ? 'shop-hero--loading' : ''} ${!shopHeroImage ? 'shop-hero--empty' : ''}`}
        style={shopHeroImage ? { backgroundImage: `url(${shopHeroImage})` } : undefined}
      >
        {isLoadingHero && <div className="shop-hero__skeleton skeleton" aria-hidden="true"></div>}
        <div className="container">
          <h1 className="shop-hero__title">SHOP</h1>
          <p className="shop-hero__subtitle">Discover our curated collection of premium abayas, crafted with timeless elegance and refined attention to detail</p>
        </div>
      </section>

      <section className="shop-section">
        <div className="shop-container">
          <div className="glass-panel">
            {/* Category Filters */}
            {isLoadingCategories ? (
              <div className="category-filters category-filters--loading" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={`category-skeleton-${index}`} className="category-filter-skeleton skeleton"></div>
                ))}
              </div>
            ) : categories.length > 0 && (
              <div className="category-filters">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`category-filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setActiveSubcategory(null);
                      setDisplayCount(8);
                      updateShopParams(category.id, null);
                    }}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            )}

            {activeCategory && (
              <>
                {isLoadingSubcategories ? (
                  <div className="subcategory-filters subcategory-filters--loading" aria-hidden="true">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={`subcategory-skeleton-${index}`} className="subcategory-filter-skeleton skeleton"></div>
                    ))}
                  </div>
                ) : visibleSubcategories.length > 0 ? (
                  <div className="subcategory-filters">
                    <button
                      className={`subcategory-filter-btn ${!activeSubcategory ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSubcategory(null);
                        updateShopParams(activeCategory, null);
                        setDisplayCount(8);
                      }}
                    >
                      All
                    </button>
                    {visibleSubcategories.map(subcategory => (
                      <button
                        key={subcategory.id}
                        className={`subcategory-filter-btn ${activeSubcategory === subcategory.id ? 'active' : ''}`}
                        onClick={() => {
                          setActiveSubcategory(subcategory.id);
                          updateShopParams(activeCategory, subcategory.id);
                          setDisplayCount(8);
                        }}
                      >
                        {subcategory.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            <div className="shop-controls">
              <div className="search-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDisplayCount(8);
                }}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => {
                    setSearchQuery('');
                    setDisplayCount(8);
                  }}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
              </div>

              <div className="sort-box">
                <label htmlFor="sort-select" className="sort-label">Sort by:</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setDisplayCount(8);
                  }}
                  className="sort-select"
                >
                  {sortOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isLoading ? (
              <div className="product-grid product-grid--skeleton" aria-hidden="true">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={`product-skeleton-${index}`} className="product-card skeleton-card">
                    <div className="product-card__image skeleton"></div>
                    <div className="product-card__info">
                      <span className="skeleton skeleton-line"></span>
                      <span className="skeleton skeleton-line skeleton-line--short"></span>
                    </div>
                  </div>
                ))}
              </div>
            ) : displayedProducts.length > 0 ? (
              <>
                <div className="product-grid">
                {displayedProducts.map(product => {
                  const images = getProductImages(product);
                  const imageUrl = images[0] || DEFAULT_PRODUCT_IMAGE;
                  
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      images={images}
                      defaultImage={imageUrl || DEFAULT_PRODUCT_IMAGE}
                      onClick={() => handleProductClick(product)}
                    />
                  );
                })}
              </div>

                {hasMore && (
                  <div className="load-more-section">
                    <button 
                      className="btn btn-outline" 
                      onClick={handleLoadMore}
                    >
                      Load More ({sortedProducts.length - displayCount} remaining)
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-products">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p className="no-products-text">No products found</p>
                <p className="no-products-subtext">Try adjusting your search or filters</p>
                {(searchQuery || activeCategory) && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory(null);
                      setActiveSubcategory(null);
                      setDisplayCount(8);
                      updateShopParams(null, null);
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Shop;
